import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileManagement = ({ plannerData, onCreateAccount }) => {
  const [timeRemaining, setTimeRemaining] = useState(24 * 60 * 60); // 24 hours in seconds

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const storageFeatures = [
    {
      icon: 'Shield',
      title: 'Secure Storage',
      description: 'Your planners are encrypted and safely stored'
    },
    {
      icon: 'Smartphone',
      title: 'Access Anywhere',
      description: 'Download your planners from any device'
    },
    {
      icon: 'History',
      title: 'Version History',
      description: 'Keep track of all your planner versions'
    },
    {
      icon: 'Users',
      title: 'Easy Sharing',
      description: 'Share planners with family and friends'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Temporary Storage Notice */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Clock" size={16} className="text-warning" />
          </div>
          <div className="flex-1">
            <h3 className="font-body font-semibold text-warning mb-1">
              Temporary Storage
            </h3>
            <p className="font-body text-sm text-muted-foreground mb-2">
              Your planner will be available for download for the next{' '}
              <span className="font-semibold text-warning">
                {formatTime(timeRemaining)}
              </span>
            </p>
            <p className="font-caption text-xs text-muted-foreground">
              Create an account to save your planners permanently
            </p>
          </div>
        </div>
      </div>
      {/* Account Creation Prompt */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="UserPlus" size={24} className="text-primary" />
        </div>
        <h2 className="font-heading text-lg font-semibold text-foreground mb-2">
          Save Your Planners Forever
        </h2>
        <p className="font-body text-sm text-muted-foreground mb-6 max-w-md mx-auto">
          Create a free account to store unlimited planners, access them from anywhere, and never lose your designs.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="default"
            onClick={onCreateAccount}
            iconName="UserPlus"
            iconPosition="left"
            className="sm:w-auto"
          >
            Create Free Account
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = '/login'}
            iconName="LogIn"
            iconPosition="left"
            className="sm:w-auto"
          >
            Sign In
          </Button>
        </div>
      </div>
      {/* Storage Features */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {storageFeatures?.map((feature, index) => (
          <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/30">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name={feature?.icon} size={14} className="text-primary" />
            </div>
            <div>
              <h4 className="font-body font-medium text-sm text-foreground mb-1">
                {feature?.title}
              </h4>
              <p className="font-caption text-xs text-muted-foreground">
                {feature?.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      {/* File Info */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="FileText" size={14} />
            <span>PDF Format</span>
          </div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Download" size={14} />
            <span>Ready for Download</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileManagement;